import { Input } from "../../../components/Input/Input";
import { Switch } from "../../../components/Switch/Switch";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import type { BotsBackfillConfig } from "../config/types";
import styles from "./BotsSettings.module.css";

interface Props {
  value: BotsBackfillConfig;
  onChange: (patch: Partial<BotsBackfillConfig>) => void;
  dirty: boolean;
}

/**
 * `bots.backfill` — the guild chat a living player finds on their first visit.
 *
 * Mockup: bots-page-shell.html §4, "Chat backfill". Moved verbatim.
 */
export function BackfillSection({ value, onChange, dirty }: Props) {
  return (
    <SectionCard
      id="backfill"
      title="Chat backfill"
      apiName="bots.backfill"
      dirty={dirty}
    >
      <FieldRow
        name="Chat backfill"
        apiName="bots.backfill"
        hint="On a real player's first visit the guild chat is written back-dated, so history exists instead of an empty screen. Runs once per guild, ever."
      >
        <Switch
          checked={value.enabled}
          onChange={(next) => onChange({ enabled: next })}
          label={value.enabled ? "On" : "Off"}
        />
        <div className={styles.pair}>
          <span className={styles.unit}>depth</span>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.depth_hours)}
            onChange={(e) => onChange({ depth_hours: Number(e.target.value) })}
            aria-label="Backfill depth"
          />
          <span className={styles.unit}>h</span>
          <span className={styles.unit}>messages</span>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.min_messages)}
            onChange={(e) => onChange({ min_messages: Number(e.target.value) })}
            aria-label="Backfill minimum messages"
          />
          <span className={styles.dash}>—</span>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.max_messages)}
            onChange={(e) => onChange({ max_messages: Number(e.target.value) })}
            aria-label="Backfill maximum messages"
          />
        </div>
        <div className={styles.helper}>
          Timestamps are spread with the same time-of-day curve as live chatter,
          so the backfilled day looks like a day.
        </div>
      </FieldRow>
    </SectionCard>
  );
}
